import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Input,
  Flex,
  Menu,
  RadioGroup,
  Checkbox,
} from '@chakra-ui/react';
import { CustomButton } from './UI/CustomButton';
import { MdDelete, MdExpandMore, MdAttachFile, MdCancel, MdFileUpload } from 'react-icons/md';
import ExerciseTypeahead from './ExerciseTypeahead';
import { useConstantsQuery } from '../query/useConstantsQuery';
import { useExerciseByIdQuery } from '../query/useExerciseByIdQuery';
import { formatConstraintValue, constraintTypeToFieldMap, fieldToDisplayNameMap } from '../utils/constraintFormat';
import { useToastContext } from './UI/ToastManager';
import { API_URL } from '../utils/graphqlRequest';

interface ValueChangeDetails<T = string> {
  value: T;
}

// Dynamic constraint options will be built from constants query

interface ExerciseFormPanelProps {
  selectedNode: { id: string; label: string; children?: any[] } | null;
  formData: any;
  onChange: (data: any) => void;
  onBlur?: (data: any) => void;
}

function isExerciseOption(node: { id: string; label: string; children?: any[] } | null): boolean {
  return node !== null && !node.children;
}

const ExerciseFormPanel: React.FC<ExerciseFormPanelProps> = ({ selectedNode, formData, onChange, onBlur }) => {
  // Fetch constants from database
  const { data: constantsData, isLoading: isLoadingConstants } = useConstantsQuery();
  const { showError } = useToastContext();
  
  // Fetch exercise details when exerciseId is available
  const { data: exerciseDetails } = useExerciseByIdQuery(formData?.exerciseId || null);

  // State for file uploads
  const [videoType, setVideoType] = useState<'upload' | 'url'>('upload');
  const [posterType, setPosterType] = useState<'upload' | 'url'>('url');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Upload function
  const uploadMedia = async (file: File, type: 'video' | 'image'): Promise<string | null> => {
    // Create the GraphQL query for upload
    const mutation = type === 'video' ? `
      mutation uploadVideo($video: Upload!, $videoType: String!) {
        uploadVideo(video: $video, videoType: $videoType) {
          success
          message
        }
      }
    ` : `
      mutation uploadImage($image: Upload!, $imageType: String!) {
        uploadImage(image: $image, imageType: $imageType) {
          success
          message
        }
      }
    `;

    // Create the operations map
    const operations = {
      query: mutation,
      variables: {
        [type === 'video' ? 'video' : 'image']: null,
        [type === 'video' ? 'videoType' : 'imageType']: type === 'video' ? 'exercise_video' : 'exercise_poster'
      }
    };

    // Create the file map
    const map = {
      [type === 'video' ? '0' : '0']: [`variables.${type === 'video' ? 'video' : 'image'}`]
    };

    const formData = new FormData();
    formData.append('operations', JSON.stringify(operations));
    formData.append('map', JSON.stringify(map));
    formData.append('0', file);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        body: formData,
      });
      
      const result = await response.json();
      
      if (result.data?.uploadVideo?.success || result.data?.uploadImage?.success) {
        // The response should contain the uploaded file URL
        const uploadResult = result.data?.uploadVideo || result.data?.uploadImage;
        return uploadResult?.message || uploadResult?.url || uploadResult?.publicUrl;
      } else {
        const errorMessage = result.data?.uploadVideo?.message || result.data?.uploadImage?.message || 'Upload failed';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Upload error:', error);
      showError('Upload Failed', `Failed to upload ${type}. Please try again.`);
      return null;
    }
  };

  // Update form data with full exercise details when they're fetched
  useEffect(() => {
    if (exerciseDetails && formData?.exerciseId === exerciseDetails.id) {
      console.log('Updating form data with exercise details:', exerciseDetails);
      onChange({
        ...formData,
        exerciseDetails: exerciseDetails
      });
    }
  }, [exerciseDetails, formData?.exerciseId, onChange]);

  // Build dynamic constraint options from constants data
  const allConstraintOptions = constantsData ? [
    { type: 'Gender', options: constantsData.genders?.map(formatConstraintValue) || [] },
    { type: 'Pathology', options: constantsData.pathologies?.map(formatConstraintValue) || [] },
    { type: 'Sport', options: constantsData.programSports?.map(formatConstraintValue) || [] },
    { type: 'Special program', options: constantsData.specialPrograms?.map(formatConstraintValue) || [] },
    { type: 'Week focus', options: constantsData.weekFocus?.map(formatConstraintValue) || [] },
    { type: 'User level', options: constantsData.userLevels?.map(formatConstraintValue) || [] },
    { type: 'Category', options: constantsData.categories?.map(formatConstraintValue) || [] },
    { type: 'Material', options: constantsData.materials?.map(formatConstraintValue) || [] },
  ] : [];

  // Get constraints from formData for exercise options
  const exerciseConstraints = selectedNode && !selectedNode.children 
    ? (Array.isArray(formData?.constraints) ? formData.constraints : [])
    : ([] as { type: string; values: string[] }[]);
    
  const availableConstraintTypes = allConstraintOptions
    .map((opt) => opt.type)
    .filter((type: string) => !exerciseConstraints.some((c: any) => c.type === type));
    
  console.log('ExerciseFormPanel - formData:', formData);
  console.log('ExerciseFormPanel - exerciseConstraints:', exerciseConstraints);

  // Handler to add a new constraint type
  const handleAddConstraint = (type: string) => {
    if (selectedNode && !selectedNode.children) {
      // For exercise options, save to formData
      const newConstraints = [...exerciseConstraints, { type, values: [] }];
      onChange({ ...formData, constraints: newConstraints });
    }
  };

  // Handler to remove a constraint
  const handleRemoveConstraint = (type: string) => {
    if (selectedNode && !selectedNode.children) {
      // For exercise options, save to formData
      const newConstraints = exerciseConstraints.filter((c: any) => c.type !== type);
      onChange({ ...formData, constraints: newConstraints });
    }
  };

  // Helper function to convert formatted value back to original format
  const convertFormattedValueToOriginal = (formattedValue: string, type: string): string => {
    const fieldName = constraintTypeToFieldMap[type];
    if (!fieldName || !constantsData) return formattedValue;
    
    const originalValues = constantsData[fieldName as keyof typeof constantsData] as string[];
    if (!originalValues) return formattedValue;
    
    // Find the original value that matches the formatted value
    const originalValue = originalValues.find(val => formatConstraintValue(val) === formattedValue);
    return originalValue || formattedValue;
  };

  // Helper function to convert original value to formatted value
  const convertOriginalValueToFormatted = (originalValue: string): string => {
    return formatConstraintValue(originalValue);
  };

  // Handler to add a value to a constraint
  const handleAddConstraintValue = (type: string, formattedValue: string) => {
    if (selectedNode && !selectedNode.children) {
      // Convert formatted value back to original format for storage
      const originalValue = convertFormattedValueToOriginal(formattedValue, type);
      
      // For exercise options, save to formData
      const newConstraints = exerciseConstraints.map((c: any) => 
        c.type === type 
          ? { ...c, values: [...c.values, originalValue] }
          : c
      );
      onChange({ ...formData, constraints: newConstraints });
    }
  };

  // Handler to remove a value from a constraint
  const handleRemoveConstraintValue = (type: string, formattedValue: string) => {
    if (selectedNode && !selectedNode.children) {
      // Convert formatted value back to original format for removal
      const originalValue = convertFormattedValueToOriginal(formattedValue, type);
      
      // For exercise options, save to formData
      const newConstraints = exerciseConstraints.map((c: any) => 
        c.type === type 
          ? { ...c, values: c.values.filter((v: any) => v !== originalValue) }
          : c
      );
      onChange({ ...formData, constraints: newConstraints });
    }
  };

  if (!selectedNode) {
    return (
      <Box p={8} flex={1}>
        <Text fontSize="xl" fontWeight="bold" mb={4}>No Exercise Option Selected</Text>
        <Text color="gray.500">Please select an exercise option from the tree to edit its details.</Text>
      </Box>
    );
  }

  if (isExerciseOption(selectedNode)) {
    // Exercise Option form
    const exerciseName = selectedNode.label;
    return (
      <Box p={8} flex={1} overflowY='auto'>
        <Flex align="center" justify="space-between" mb={6}>
          <Text fontSize="2xl" fontWeight="bold">{exerciseName}</Text>
        </Flex>

        {/* Select Exercise Section */}
        <Box mb={6}>
          <Text fontWeight="medium" mb={2}>Select exercise</Text>
          <ExerciseTypeahead
            value={formData?.exerciseName || ''}
            onChange={(value, exerciseId) => {
              const newFormData = { 
                ...formData, 
                exerciseName: value,
                exerciseId: exerciseId || formData?.exerciseId
              };
              onChange(newFormData);
            }}
            placeholder="Search exercises..."
          />
        </Box>

        {/* Numerical Input Fields */}
        <Flex gap={6} mb={6}>
          <Box flex={1}>
            <Text fontWeight="medium" mb='8px'>Repetition</Text>
            <Input
              placeholder="Number of repetitions"
              value={formData?.repetitions !== undefined ? formData.repetitions : ''}
              onChange={e => onChange({ ...formData, repetitions: e.target.value })}
            />
          </Box>
          <Box flex={1}>
            <Text fontWeight="medium" mb='8px'>Series</Text>
            <Input
              placeholder="Number of series"
              value={formData?.series !== undefined ? formData.series : ''}
              onChange={e => onChange({ ...formData, series: e.target.value })}
            />
          </Box>
        </Flex>

        <Flex gap={6} mb={6}>
          <Box flex={1}>
            <Text fontWeight="medium" mb='8px'>Duration</Text>
            <Input
              placeholder="Duration in seconds"
              value={formData?.duration !== undefined ? formData.duration : ''}
              onChange={e => onChange({ ...formData, duration: e.target.value })}
            />
          </Box>
          <Box flex={1}>
            <Text fontWeight="medium" mb='8px'>Recuperation</Text>
            <Input
              placeholder="Recuperation in seconds"
              value={formData?.recuperation !== undefined ? formData.recuperation : ''}
              onChange={e => onChange({ ...formData, recuperation: e.target.value })}
            />
          </Box>
        </Flex>

        {/* Constraints Section */}
        <Text fontWeight="bold" fontSize="lg" mb={4}>Constraints Exercise</Text>

        <Box mb={6} borderWidth={1} borderRadius="lg" p={4} maxH='238px' overflowY='auto'>
          
          {/* Dynamic constraint multiselects */}
          {exerciseConstraints.map((constraint: any) => {
            console.log('Rendering constraint:', constraint);
            const config = allConstraintOptions.find(opt => opt.type === constraint.type);
            console.log('Found config for constraint type:', constraint.type, 'config:', config);
            if (!config) {
              console.log('No config found for constraint type:', constraint.type);
              return null;
            }
            
            // Convert stored original values to formatted values for display
            const formattedSelectedValues = constraint.values.map(convertOriginalValueToFormatted);
            console.log('Formatted selected values:', formattedSelectedValues);
            const availableValues = config.options.filter((opt: string) => !formattedSelectedValues.includes(opt));
            console.log('Available values:', availableValues);
            
            return (
              <Flex align="center" mb={4} gap={4} key={constraint.type}>
                <Box flex={1}>
                  <Text mb={1} fontWeight="medium">{constraint.type}</Text>
                  <Menu.Root>
                    <Menu.Trigger style={{ width: '100%' }}>
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        p={2}
                        minH="48px"
                        display="flex"
                        flexWrap="wrap"
                        gap={1}
                        alignItems="center"
                        bg="white"
                        position="relative"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                      >
                        {/* Selected values as tags */}
                        {formattedSelectedValues.map((formattedValue: any, idx: any) => (
                          <Flex
                            key={idx}
                            align="center"
                            bg="gray.100"
                            borderRadius="full"
                            px={2}
                            py={1}
                            gap={1}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Text fontSize="sm" color="gray.700">{formattedValue}</Text>
                            <Box
                              as="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveConstraintValue(constraint.type, formattedValue);
                              }}
                              cursor="pointer"
                              color="gray.500"
                              _hover={{ color: 'gray.700' }}
                              fontSize="12px"
                              fontWeight="bold"
                            >
                              ×
                            </Box>
                          </Flex>
                        ))}
                        
                        {/* Placeholder when no values selected */}
                        {constraint.values.length === 0 && (
                          <Text color="gray.400" fontSize="sm">
                            Select {constraint.type}
                          </Text>
                        )}
                        
                        {/* Dropdown for remaining values */}
                        {availableValues.length > 0 && (
                          <Box
                            position="absolute"
                            right={2}
                            top="50%"
                            transform="translateY(-50%)"
                            display="flex"
                            alignItems="center"
                            color="gray.400"
                            pointerEvents="none"
                          >
                            <MdExpandMore />
                          </Box>
                        )}
                      </Box>
                    </Menu.Trigger>
                    <Menu.Positioner>
                      <Menu.Content>
                        {availableValues.map((value: string) => (
                          <Menu.Item 
                            key={value} 
                            onClick={() => handleAddConstraintValue(constraint.type, value)}
                          >
                            {value}
                          </Menu.Item>
                        ))}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Menu.Root>
                </Box>
                
                {/* Remove entire constraint button */}
                <CustomButton
                  visual="ghost"
                  alignSelf='flex-end'
                  aria-label={`Remove ${constraint.type}`}
                  onClick={() => handleRemoveConstraint(constraint.type)}
                  _hover={{ background: '#fc3c5ecc' }}
                  w='56px'
                  border='1px solid #DADADA'
                  borderRadius='5px'
                  h='48px'
                >
                  <MdDelete />
                </CustomButton>
              </Flex>
            );
          })}
          
          {/* Add new constraint type */}
          {availableConstraintTypes.length > 0 && (
            <Box w="100%">
              <Text mb={1} fontWeight="medium">Select constraints</Text>
              <Menu.Root w="100%">
                <Menu.Trigger style={{ width: '100%' }}>
                  <Box
                    w="100%"
                    px={4}
                    py={2}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="white"
                    textAlign="left"
                    cursor="pointer"
                    position="relative"
                    minH="48px"
                    display="flex"
                    alignItems="center"
                    color="gray.400"
                    _hover={{ bg: 'gray.50' }}
                  >
                    {availableConstraintTypes.join(', ')}
                    <Box position="absolute" right={3} top="50%" transform="translateY(-50%)" pointerEvents="none">
                      <MdExpandMore />
                    </Box>
                  </Box>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content>
                    {availableConstraintTypes.map((type: string) => (
                      <Menu.Item key={type} onClick={() => handleAddConstraint(type)}>
                        {type}
                      </Menu.Item>
                    ))}
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // If an Exercise (not Exercise Option) is selected, show a message
  return (
    <Box p={8} flex={1}>
      <Text fontSize="xl" fontWeight="bold" mb={4}>Exercise Selected</Text>
      <Text color="gray.500">Please select an exercise option to edit its details.</Text>
    </Box>
  );
};

export default ExerciseFormPanel; 